import React, {useCallback, useMemo} from 'react';
import {NodeProps} from 'reactflow';

import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import getTestProps from 'test/lib/cypress/getTestProps';
import useHoverState from 'ui-library/hooks/useHoverState';
import {ElementMouseEvent} from 'util/Types';

import {
  NodeLocationType,
  ProcedureEndsMethodNodeAddNodeButton,
} from './buttons/ProcedureEndsMethodNodeAddNodeButton';
import {createHandleIdForMethod} from './handleIdentifierUtils';
import {MethodNodeNameSection} from './MethodNodeNameSection';
import {ProcedureEditorContext} from './ProcedureEditorContext';
import {makeNodeWrapperWithOpacityClass, methodBodyClass} from './procedureMethodNodeCommonConsts';
import {
  ProcedureEquipmentMethodSection,
  ProcedureMaterialsMethodSection,
  ProcedureParametersMethodSection,
} from './procedureMethodNodeRendererSections';
import {
  FilteredProcedureMethodSection,
  getSectionOptionUIString,
} from './ProcedureMethodSectionFilterDropdown';
import {MethodCanvasNodeData} from './ProcedureReactFlowCanvasTypes';
import {useGridLayoutNodeDOMRefCallback} from './useConvertedGridLayoutGraph';
import {useMethodNameEditing} from './useMethodNameEditing';
import {useMethodNodeValidation} from './useMethodNodeValidation';

export default function ProcedureMethodNodeRenderer(props: NodeProps<MethodCanvasNodeData>): JSX.Element {
  const methodCid = props.data.cid;

  const {accessLevel, setLegacyShouldPauseAutosave, procedureMetadata} = ProcedurePageContext.useContext();
  const procedureType = procedureMetadata.procedureType;
  const hasFullAccess = accessLevel === ProcedureAccessLevel.FULL_ACCESS;

  const [isHovered, hoverProps] = useHoverState();
  const {
    handlesInUse,
    editingNameMethodCid,
    methodCidBeingDragged,
    selectedMethodSectionFilter,
    isProcedureReadOnly,
  } = ProcedureEditorContext.useContext();
  const parameterUIString = useMemo(
    () => getSectionOptionUIString(FilteredProcedureMethodSection.PARAMETERS),
    []
  );
  const equipmentUIString = useMemo(
    () => getSectionOptionUIString(FilteredProcedureMethodSection.EQUIPMENT),
    []
  );

  const nameEditing = useMethodNameEditing(methodCid, procedureType, setLegacyShouldPauseAutosave);
  const hasValidationErrors = useMethodNodeValidation(methodCid);

  const gridDomRefCallback = useGridLayoutNodeDOMRefCallback();
  const refCallback = useCallback(
    (domNode: HTMLElement | null) => {
      gridDomRefCallback(methodCid, domNode);
    },
    [methodCid, gridDomRefCallback]
  );
  const isLeafNode = !handlesInUse.includes(createHandleIdForMethod(methodCid, 'source'));
  const isRootNode = !handlesInUse.includes(createHandleIdForMethod(methodCid, 'target'));
  const shouldRenderMethodSection = (sectionName: FilteredProcedureMethodSection): boolean =>
    selectedMethodSectionFilter.includes(sectionName);

  function handleDoubleClick(e: ElementMouseEvent): void {
    if (editingNameMethodCid == null && hasFullAccess) {
      e.stopPropagation();
      props.data.onEditMethod(methodCid);
    }
  }

  return (
    <div
      onDoubleClick={handleDoubleClick}
      className={nameEditing.wrapperNoDragClass}
      {...getTestProps({
        component: 'ProcedureMethodNodeRenderer',
        element: 'root',
        key: props.data.name,
      })}
    >
      {isRootNode && !isProcedureReadOnly && hasFullAccess && (
        <ProcedureEndsMethodNodeAddNodeButton
          methodCid={methodCid}
          onClick={props.data.onAddMethod}
          nodeLocationType={NodeLocationType.ROOT}
        />
      )}
      {isLeafNode && !isProcedureReadOnly && hasFullAccess && (
        <ProcedureEndsMethodNodeAddNodeButton
          methodCid={methodCid}
          onClick={props.data.onAddMethod}
          nodeLocationType={NodeLocationType.LEAF}
        />
      )}
      <div
        className={makeNodeWrapperWithOpacityClass(
          props.selected && !isProcedureReadOnly,
          methodCidBeingDragged
        )}
        ref={refCallback}
      >
        <MethodNodeNameSection
          methodCid={methodCid}
          data={props.data}
          hasFullAccess={hasFullAccess}
          isProcedureReadOnly={isProcedureReadOnly}
          hasValidationErrors={hasValidationErrors}
          nameEditing={nameEditing}
        />
        <div
          className={methodBodyClass(hasValidationErrors)}
          {...getTestProps({component: 'ProcedureMethodNodeRenderer', element: 'body'})}
          {...hoverProps}
        >
          {shouldRenderMethodSection(FilteredProcedureMethodSection.MATERIALS) && (
            <ProcedureMaterialsMethodSection
              sectionTitle="Material"
              isHovered={isHovered}
              isLeafNode={isLeafNode}
              data={props.data}
              handlesInUse={handlesInUse}
              methodCid={methodCid}
              isProcedureReadOnly={isProcedureReadOnly}
              accessLevel={accessLevel}
            />
          )}
          {shouldRenderMethodSection(FilteredProcedureMethodSection.PARAMETERS) && (
            <ProcedureParametersMethodSection sectionTitle={parameterUIString} data={props.data} />
          )}
          {shouldRenderMethodSection(FilteredProcedureMethodSection.EQUIPMENT) && (
            <ProcedureEquipmentMethodSection sectionTitle={equipmentUIString} data={props.data} />
          )}
        </div>
      </div>
    </div>
  );
}
